import { T } from 'src/shared/helpers';
import React, { FC } from 'react';
import styled from 'styled-components';
import { BasicButton, GhostButton } from '../../../../../widgets/buttons';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import CommandMenu from './commandMenu';
import { softAccentButton } from '../editorPalette';
import { DotsIcon } from '../../../../../widgets/icons/scene';
import { ConfirmText, ConfirmTwice, confirmLabel } from '../confirmTwice';
import { ZTooltip } from '../../../../../widgets/ztooltip';

export type SceneEditorActionKind = 'add' | 'select' | 'action' | 'danger';

export interface SceneEditorAction {
    /**
     * COME SI CHIAMA IL COMANDO, in due parole: «Duplicate», «Remove». È il nome che si legge —
     * nel «…» di una riga e nel pannello del mucchio — e quello che la barra dà da leggere a chi
     * non vede il glifo. Un nome per macchine («CLONE») lì diventa una parola gridata.
     */
    name: string;
    /** La riga che spiega cosa fa: «A copy in this scene». Sotto il nome nel menu, in punta al puntatore nella barra. */
    tooltip: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    kind?: SceneEditorActionKind;
    /**
     * Per i comandi che accendono e spengono qualcosa invece di eseguirla una volta:
     * il bottone resta premuto finché la condizione vale, e lo dichiara con `aria-pressed`.
     */
    active?: boolean;
    /**
     * QUANDO IL COMANDO HA PIÙ STRADE. Un modello arriva da un caricamento o dalla libreria, una
     * luce nasce di quattro tipi: quale lo dice chi clicca, e il bottone apre un menu invece di
     * eseguire. `onClick` resta come ripiego se il menu è vuoto, e non viene mai chiamato finché
     * c'è anche una sola voce.
     */
    menu?: SceneEditorAction[];
}

interface SceneEditorActionBarProps {
    actions: SceneEditorAction[];
    /** Le viste della libreria (List/Grid): stanno prima dei comandi dell'elenco. */
    trailing?: React.ReactNode;
    /** I comandi del «…» della selezione: vuoto, il «…» c'è ma è spento. Assente, non c'è. */
    selection?: SceneEditorAction[];
}

// LA BARRA VA A CAPO, NON NASCONDE. Scorreva in orizzontale con la barra di scorrimento
// invisibile: bastava un comando in più — «Seleziona tutto» — perché l'ultimo, il cestino,
// finisse fuori dal bordo senza alcun segno che ci fosse ancora. Un comando nascosto senza
// avviso è un comando perduto. Andando a capo la riga si allunga quando serve, e nella
// maggior parte delle sezioni non serve.
const ActionBar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 3px;
    min-height: 40px;
    padding: 4px 6px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const ActionButton = styled(GhostButton)<{ $active?: boolean }>`
    flex: 0 0 auto;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    padding: 0 6px;
    border-radius: 8px;
    font-size: 12px;
    /* PREMUTO NELL'ARANCIO TENUE, come nel Composer (ricetta «icona di barra», utente 2026-10-20). */
    background: ${(props) => (props.$active ? 'var(--editor-accent-wash, #f6efe9)' : 'transparent')};
    color: ${(props) => (props.$active ? 'var(--editor-accent, #a94700)' : 'inherit')};

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// «Aggiungi» non è l'azione principale della pagina — quella è Save, in fascia. Qui è la
// prima voce di una barra fra tante barre uguali, e dipingerla d'arancione metteva quattro
// primari sullo schermo che si contendevano lo stesso significato.
const AddActionButton = styled(BasicButton)`
    ${softAccentButton}
    flex: 0 0 auto;
    height: 32px;
    min-height: 32px;
    padding: 0 9px;
    border-radius: 8px;
    font-size: 12px;
    gap: 4px;

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// Il distruttivo si riconosce dal COLORE del glifo, non da un rettangolo rosso: pieno,
// pesa più del comando che lo precede e attira il clic che non deve attirare.
const DestructiveActionButton = styled(GhostButton)`
    flex: 0 0 auto;
    min-width: 30px;
    height: 32px;
    min-height: 32px;
    padding: 0 6px;
    border-radius: 8px;
    font-size: 12px;
    /* UN SOLO ROSSO NEGLI EDITOR (ricetta «pericolo»): quello della conferma. */
    color: var(--editor-danger, #c33a31);

    &:hover:not([disabled]),
    &:active:not([disabled]) {
        color: var(--editor-danger, #c33a31);
    }

    > svg {
        width: 17px;
        height: 17px;
        margin: 0;
    }
`;

// Il vuoto fra «Aggiungi» e il resto: i comandi che agiscono su una selezione stanno
// dall'altra parte della barra, e il distruttivo chiude la fila.
const ActionSpacer = styled.span`
    flex: 1 1 auto;
`;

const ActionDivider = styled.span`
    width: 1px;
    height: 24px;
    margin: 0 3px;
    background: ${(props) => props.theme.app.borderColor};
`;

// La freccetta che dice «questo bottone apre un menu, non esegue». Senza, chi guarda
// non sa che c'è una scelta da fare finché non clicca.
const DropdownChevron = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: -2px;
    margin-right: -4px;

    > svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

// IL «…» DELLA SELEZIONE (prototipo `UI.selMore`): le azioni su ciò che è scelto, sempre al suo posto in fondo
// alla barra. Senza niente di scelto è spento, non sparisce: una barra che cambia forma sposta i comandi sotto
// il puntatore.
const SelectionMore: FC<{ actions: SceneEditorAction[] }> = ({ actions }) => {
    const usable = actions.filter((action) => !action.disabled);
    const button = (
        <ActionButton type='button' disabled={!usable.length} aria-label={T._('Selection commands', 'Admin')}>
            <DotsIcon />
        </ActionButton>
    );

    // Spento, il bottone non riceve il puntatore: il tooltip si appoggia a un contenitore che lo riceve.
    return usable.length ? (
        <CommandMenu
            actions={actions}
            trigger={
                <ZTooltip title={T._('Selection commands', 'Admin')} position='bottom'>
                    {button}
                </ZTooltip>
            }
            captions={false}
        />
    ) : (
        <ZTooltip title={T._('Select something in the list first', 'Admin')} position='bottom' addContainer>
            {button}
        </ZTooltip>
    );
};

const SceneEditorActionBar: FC<SceneEditorActionBarProps> = ({ actions, trailing, selection }) => {
    const renderAction = (action: SceneEditorAction) => {
        const kind = action.kind ?? 'action';
        const showLabel = kind === 'add';

        const buttonProps = {
            type: 'button' as const,
            // CON UN MENU SOTTO, IL BOTTONE APRE E BASTA. Lasciandogli anche il suo `onClick` si
            // otteneva un clic che faceva le due cose insieme — «Add object» spalancava la
            // scelta dei file *e* apriva il menu — perché il clic parte dal bottone e sale al
            // guscio che comanda il menu.
            onClick: action.menu?.length ? undefined : action.onClick,
            disabled: action.disabled,
            'aria-label': action.name,
            'aria-pressed': action.active === undefined ? undefined : action.active
        };

        const hasMenu = !!action.menu?.length;

        const content = (
            <>
                {action.icon}
                {showLabel && <span>{action.name}</span>}
                {hasMenu && (
                    <DropdownChevron aria-hidden='true'>
                        <ExpandMoreIcon />
                    </DropdownChevron>
                )}
            </>
        );

        const button =
            kind === 'add' ? (
                <AddActionButton {...buttonProps}>{content}</AddActionButton>
            ) : kind === 'danger' && !hasMenu ? (
                // Rimuovere chiede due volte, nel bottone stesso (confirmTwice).
                <ConfirmTwice onConfirm={action.onClick}>
                    {({ armed, ...confirm }) => (
                        // Armato, il nome è la domanda che si legge: il nome del comando la copriva.
                        <DestructiveActionButton {...buttonProps} {...confirm} aria-label={armed ? `${action.name}: ${confirmLabel()}` : action.name}>
                            {action.icon}
                            {armed && <ConfirmText>{confirmLabel()}</ConfirmText>}
                        </DestructiveActionButton>
                    )}
                </ConfirmTwice>
            ) : kind === 'danger' ? (
                <DestructiveActionButton {...buttonProps}>{content}</DestructiveActionButton>
            ) : (
                <ActionButton {...buttonProps} $active={action.active}>
                    {content}
                </ActionButton>
            );

        // IL TOOLTIP È QUELLO DEGLI EDITOR, non il `title` del browser (utente, 2026-10-21: grande, arrotondato, diverso
        // da tutti gli altri). Spento, il bottone non riceve il puntatore: il tooltip si appoggia a un contenitore.
        const tipped = action.tooltip ? (
            <ZTooltip title={action.tooltip} position='bottom' addContainer={!!action.disabled || kind === 'danger'}>
                {button}
            </ZTooltip>
        ) : (
            button
        );

        return (
            <React.Fragment key={action.name}>
                {hasMenu ? <CommandMenu actions={action.menu!} trigger={tipped} orientation={kind === 'add' ? 'LEFT' : 'RIGHT'} /> : tipped}
            </React.Fragment>
        );
    };

    // LA TESTATA È UGUALE IN OGNI ELENCO (prototipo, AGGIORNAMENTI §4; utente, 2026-10-20): Add a sinistra, come
    // sempre da noi; a destra Select all, un divisore, i comandi dell'elenco (la vista, poi un ramo alla volta,
    // Collapse per ultimo) e in fondo il «…» della selezione.
    const kindOf = (action: SceneEditorAction) => action.kind ?? 'action';
    const adds = actions.filter((action) => kindOf(action) === 'add');
    const selects = actions.filter((action) => kindOf(action) === 'select');
    const commands = actions.filter((action) => kindOf(action) !== 'add' && kindOf(action) !== 'select');

    return (
        <ActionBar role='toolbar' aria-label={T._('Scene editor actions', 'Admin')}>
            {adds.map(renderAction)}
            <ActionSpacer aria-hidden='true' />
            {selects.map(renderAction)}
            {selects.length > 0 && (trailing || commands.length > 0 || selection) && <ActionDivider aria-hidden='true' />}
            {trailing}
            {commands.map(renderAction)}
            {selection && <SelectionMore actions={selection} />}
        </ActionBar>
    );
};

export default SceneEditorActionBar;
